import * as React from 'react'
import getConfig from 'next/config'

import {
  Country,
  Currency,
  Flag,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { sanitizeCookie } from '@thg-commerce/enterprise-utils'
import { useSubscribableRef } from '@thg-commerce/enterprise-utils/src/SubscribableRef'

import { EnterpriseContext } from './EnterpriseContext'
import { HorizonSessionSettings } from './types'

export const useExtensions = () => {
  const { extensionsRef } = React.useContext(EnterpriseContext)
  return useSubscribableRef(extensionsRef)
}

export const useExperiments = () => {
  const { extensionsRef } = React.useContext(EnterpriseContext)
  const extensions = useSubscribableRef(extensionsRef)
  const experiments = React.useMemo(() => {
    let finalExperiments = extensions?.experiments || {}

    if (Object.keys(finalExperiments).length === 0) {
      const cookieValue = sanitizeCookie('enterprise_experiments')

      if (cookieValue) {
        try {
          finalExperiments = cookieValue
            .split(',')
            .reduce((acc, experiment) => {
              const [name, bucket] = experiment.split(':')
              if (name && bucket) {
                acc[name] = bucket
              }
              return acc
            }, {})
        } catch (e) {
          console.error('Error parsing experiments cookie:', e)
        }
      }
    }

    return finalExperiments
  }, [extensions])

  return experiments
}

export const useFlags = () => {
  const extensions = useExtensions()

  if (extensions?.flags?.length) {
    return extensions?.flags
  }

  const { publicRuntimeConfig } = getConfig()
  return publicRuntimeConfig.FLAGS || []
}

export const useHeaderHeight = () => {
  const { headerHeightRef } = React.useContext(EnterpriseContext)
  return useSubscribableRef(headerHeightRef)
}

export const useHorizonSessionSettings = (): HorizonSessionSettings => {
  const { currency, shippingDestination } = React.useContext(EnterpriseContext)

  return {
    currency: currency as Currency,
    shippingDestination: shippingDestination?.code as Country,
  }
}

export const useEnterpriseContext = () => {
  return React.useContext(EnterpriseContext)
}

export const useTheme = () => {
  const { theme } = useEnterpriseContext()
  return theme
}
